// Web transaction line kinds, as returned by the server's getTransactionUsers
// (`product_type` + `camp_ind`) and /details (`batch`, `camp`, `school_program`,
// `event`).
// camp_ind follows the course/batch_type convention: 0 class, 1 camp, 2 school
// program. Events also carry camp_ind 0, so product_type is checked first.

export const PRODUCT_TYPE_LABELS = {
  program: "Class",
  camp: "Camp",
  event: "Event",
  school_program: "School Program",
};

// The list's Type column and filter, keyed by the server's `transaction_types`
// (GET /:branchId), in display order. `variant` is the react-bootstrap Badge
// colour for each kind.
export const TRANSACTION_TYPES = [
  { value: "course", label: "Course", variant: "primary" },
  { value: "camp", label: "Camp", variant: "warning" },
  { value: "event", label: "Event", variant: "success" },
  { value: "school_program", label: "School Program", variant: "info" },
];

export const isSchoolProgramLine = (line) =>
  !!line &&
  (line.product_type === "school_program" ||
    (line.product_type == null && Number(line.camp_ind) === 2));

// "<school_name> – <day>". Prefers the server's precomputed label when present.
export const schoolProgramSection = (line) =>
  (line && line.section_label) ||
  [line && line.school_name, line && line.day].filter(Boolean).join(" – ");

const range = (from, to, sep) =>
  from && to ? `${from}${sep}${to}` : from || to || "";

const joinSegments = (segments, sep = " ") =>
  segments.filter((seg) => seg != null && seg !== "").join(sep);

// One entry of a student's `courses` in the enroll form's Courses column.
// Classes, camps and events render their course name as before; school
// program lines add school, day and dates plus a kind tag so staff can tell
// them apart. Dates are shown only when both ends are present.
export const formatStudentCourse = (course) => {
  if (!isSchoolProgramLine(course)) return course.courseName;
  const dates =
    course.startdate && course.enddate
      ? `${course.startdate} – ${course.enddate}`
      : "";
  const where = joinSegments([schoolProgramSection(course), dates], ", ");
  return joinSegments([
    course.courseName,
    where && `@ ${where}`,
    `(${PRODUCT_TYPE_LABELS.school_program})`,
  ]);
};

// Price as money, or "" when the server sent none. quantity is deliberately
// not shown: it's the cart payment line's quantity, shared by every student
// on that program, not a per-student count.
const formatPrice = (price) =>
  price == null || price === "" || isNaN(Number(price))
    ? ""
    : `$${Number(price).toFixed(2)}`;

// One /details school_program row for the expandable list row.
export const formatSchoolProgramDetail = (line) => {
  const name = joinSegments([line.student_firstname, line.student_lastname]);
  const section = schoolProgramSection(line);
  const course = joinSegments([
    line.course_abbr || line.course_name,
    line.course_code,
  ]);
  return joinSegments([
    name,
    section && (name ? `@ ${section}` : section),
    range(line.start_time, line.end_time, " - "),
    range(line.startdate, line.enddate, " to "),
    course && `[${course}]`,
    formatPrice(line.price),
  ]);
};

// One /details event row: student, event name, times, dates and price. A
// one-day event shows its date once rather than "date to date".
export const formatEventDetail = (line) => {
  const name = joinSegments([line.student_firstname, line.student_lastname]);
  const dates =
    line.startdate && line.startdate === line.enddate
      ? line.startdate
      : range(line.startdate, line.enddate, " to ");
  return joinSegments([
    name,
    line.workshop_name && (name ? `@ ${line.workshop_name}` : line.workshop_name),
    range(line.start_time, line.end_time, " - "),
    dates,
    formatPrice(line.price),
  ]);
};
